import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import ReviewCard from "../components/ReviewCard";
import { TrustList } from "../components/TrustList";
import { ExternalLink, Star } from "lucide-react";
import { reviews, businessInfo, pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const reviewsSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "TOFT Gas, Heating & Plumbing",
  url: `${SITE_URL}/reviews`,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "5",
    bestRating: "5",
  },
  review: reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
    reviewBody: r.text,
  })),
};

const ReviewsPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.reviews} jsonLd={reviewsSchema} />
      <PageHero
        eyebrow="Customer Reviews"
        image={pageHeroImages.reviews}
        title="Trusted By Homeowners, Landlords &amp; HMO Owners Across Cardiff &amp; South Wales"
        subtitle="5.0★ rated Gas Safe engineer covering Cardiff, Pontypridd, Porth, Bridgend and Caerphilly. Much of our work comes from repeat customers and recommendations — here's what some of our customers have to say."
      />

      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-lg font-bold text-brand-navy">5.0 Rating</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
            {reviews.map((r, i) => (
              <ReviewCard review={r} key={r.id} featured={i === 0} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-7">
            Why Customers Choose TOFT
          </h2>
          <TrustList
            items={[
              "Gas Safe Registered",
              "Honest Advice",
              "Reliable Appointments",
              "Friendly Service",
              "Fast Response Times",
              "Trusted By Homeowners & Landlords",
              "Easy Contact By Phone, WhatsApp & Text",
              "Based In Porth",
            ]}
            columns={2}
          />
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
            A Business Built on Recommendations
          </h2>
          <div className="space-y-3 text-base sm:text-lg text-slate-700 leading-relaxed mb-10">
            <p>We believe the best businesses are built on trust.</p>
            <p>
              That's why we focus on providing reliable service, clear communication and
              honest advice on every job, whether it's a boiler service, landlord gas
              safety certificate, boiler repair or heating issue.
            </p>
            <p>
              Much of our work comes from repeat customers and recommendations, and we're
              grateful for every customer who trusts us with their home or property.
            </p>
          </div>

          <div className="bg-gradient-to-br from-brand-navy to-brand-navy-light text-white rounded-2xl p-8">
            <h3 className="text-xl sm:text-2xl font-black mb-3">Leave a Review</h3>
            <p className="text-slate-200 mb-5">
              If we've completed work for you and you were happy with the service, we'd
              really appreciate a Google review. Reviews help local businesses like ours
              continue to grow and help other customers find trusted tradespeople.
            </p>
            <a
              href={businessInfo.googleReviewLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand-navy rounded-md font-semibold hover:bg-brand-blue-soft transition-colors"
            >
              Leave a Google Review
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <FinalCTA
        title="Looking for a Gas Safe engineer you can trust?"
        lines={["Need a boiler service, CP12 certificate, boiler repair or heating advice?"]}
      />
    </>
  );
};

export default ReviewsPage;
